import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { memberApi, subApi, api } from '../../config/api.config';
import Loading from '../common/Loading';
import EditMemberModal from './EditMemberModal';
import { formatAdminApiError } from '../../utils/formatAdminApiError';
import { MemberPaymentAttemptsSection } from './MemberPaymentAttemptsSection';

function MemberSubscriptionPaymentNoteCell({ subscription }) {
    const rc = subscription.retryCount || 0;
    const err = subscription.lastRetryError;
    if (!rc && !err) {
        return <span style={{ color: '#999', fontSize: 13 }}>-</span>;
    }
    return (
        <div style={{ textAlign: 'left', maxWidth: 240 }}>
            {rc > 0 && (
                <div style={{ fontSize: 11, fontWeight: 600, color: '#e65100', marginBottom: err ? 4 : 0 }}>
                    재시도 {rc}회
                </div>
            )}
            {err && (
                <div style={{ fontSize: 11, color: '#c62828', lineHeight: 1.45, wordBreak: 'break-word' }} title={err}>
                    {err}
                </div>
            )}
        </div>
    );
}

const MemberVehicles = () => {
    const { memberId } = useParams();
    const nav = useNavigate();
    const [member, setMember] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [cards, setCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);



    useEffect(() => {
        setLoading(true);
        Promise.all([
            memberApi.get(`/admin/members/${memberId}`),
            subApi.get('/admin/subscriptions?limit=1000') // 모든 구독 정보를 가져오도록 limit 증가
        ])
            .then(([memberRes, subsRes]) => {
                console.log('Member data:', memberRes.data);
                console.log('Subscriptions data:', subsRes.data);
                setMember(memberRes.data);
                setVehicles(memberRes.data.vehicles || []);
                
                // 해당 멤버의 구독만 필터링 (null 체크 추가)
                const allSubs = Array.isArray(subsRes.data)
                    ? subsRes.data
                    : (subsRes.data && subsRes.data.subscriptions) || [];
                
                // 디버깅: 해당 멤버의 모든 구독 로그
                console.log('현재 memberId:', memberId);
                console.log('모든 구독 데이터 샘플:', allSubs.slice(0, 3));
                
                const allMemberSubs = allSubs.filter(sub => {
                    const userIdMatch = sub.userId && (
                        sub.userId._id === memberId || 
                        sub.userId === memberId ||
                        (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                    );
                    console.log(`구독 ${sub._id}: userId=${JSON.stringify(sub.userId)}, memberId=${memberId}, 매치=${userIdMatch}`);
                    return userIdMatch;
                });
                console.log('해당 멤버의 모든 구독:', allMemberSubs);
                allMemberSubs.forEach(sub => {
                    console.log(`구독 ID: ${sub._id}, 상태: ${sub.status}, 차량ID: ${sub.vehicleId}`);
                });
                
                const memberSubscriptions = allSubs.filter(sub => {
                    const userIdMatch = sub.userId && (
                        sub.userId._id === memberId || 
                        sub.userId === memberId ||
                        (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                    );
                    return userIdMatch && (sub.status === 'active' || sub.status === 'cancellation_requested');
                });
                console.log('필터링된 활성 구독:', memberSubscriptions);
                setSubscriptions(memberSubscriptions);
                setLoading(false);
            })
            .catch(err => {
                console.log(err);
                alert('멤버 정보를 불러오지 못했습니다.');
                nav('/admin/members');
            });
    }, [memberId, nav]);

    const refreshMember = () => {
        memberApi.get(`/admin/members/${memberId}`)
            .then(res => {
                setMember(res.data);
            })
            .catch(err => {
                console.log('멤버 정보 갱신 실패:', err);
            });
    };

    // 카드 정보 별도로 로드 (실패해도 멤버 정보는 정상 표시)
    useEffect(() => {
        setCardsLoading(true);
        // 관리자: 대상 멤버의 카드 목록 조회
        api.get(`/payments/admin/users/${memberId}/cards`)
            .then(res => {
                console.log('Cards data:', res.data);
                setCards(res.data.cards || []);
                setCardsLoading(false);
            })
            .catch(err => {
                console.log('카드 정보 로드 실패:', err);
                setCards([]);
                setCardsLoading(false);
            });
    }, []);

    // 상품 목록 로드 (동적)
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productsError, setProductsError] = useState(null);

    useEffect(() => {
        setProductsLoading(true);
        setProductsError(null);
        api.get('/products/list')
            .then(res => {
                const list = (res.data && res.data.products) || [];
                setProducts(Array.isArray(list) ? list : []);
            })
            .catch(err => {
                setProducts([]);
                setProductsError('상품 정보를 불러오지 못했습니다.');
                console.log('상품 목록 로드 실패:', err);
            })
            .finally(() => setProductsLoading(false));
    }, []);

    const handleProductChange = (subscription) => {
        handleModalOpen(subscription);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedSubscription(null);
        setSelectedProductId(null);
        setChangeLoading(false);
    };

    const [selectedProductId, setSelectedProductId] = useState(null);
    const [changeLoading, setChangeLoading] = useState(false);
    const [selectedIncludeUnderbodyWash, setSelectedIncludeUnderbodyWash] = useState(false);

    // 결제 카드 변경 모달 상태
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [selectedSubscriptionForCard, setSelectedSubscriptionForCard] = useState(null);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [changeCardLoading, setChangeCardLoading] = useState(false);

    // 구독 활성화 모달 상태
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
    const [selectedVehicleForActivation, setSelectedVehicleForActivation] = useState(null);
    const [activationProductId, setActivationProductId] = useState(null);
    const [activationCardId, setActivationCardId] = useState(null);
    const [activationIncludeUnderbodyWash, setActivationIncludeUnderbodyWash] = useState(false);
    const [activationLoading, setActivationLoading] = useState(false);

    const handleProductChangeConfirm = (newProductId) => {
        if (!selectedSubscription) return;

        setChangeLoading(true);

        subApi.patch(`/admin/subscriptions/${selectedSubscription._id}/product`, {
            productId: newProductId,
            includeUnderbodyWash: Boolean(selectedIncludeUnderbodyWash)
        })
            .then(() => {
                alert('상품 변경이 예약되었습니다.');
                // 즉시 모달 닫기
                handleModalClose();
                // 구독 목록 새로고침
                subApi.get('/admin/subscriptions?limit=1000')
                    .then(res => {
                        const allSubs = Array.isArray(res.data)
                            ? res.data
                            : (res.data && res.data.subscriptions) || [];
                        const memberSubscriptions = allSubs.filter(sub => {
                            const userIdMatch = sub.userId && (
                                sub.userId._id === memberId || 
                                sub.userId === memberId ||
                                (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                            );
                            return userIdMatch && (sub.status === 'active' || sub.status === 'cancellation_requested');
                        });
                        setSubscriptions(memberSubscriptions);
                    });
            })
            .catch(err => {
                console.log(err);
                alert(`상품 변경에 실패했습니다.\n${formatAdminApiError(err)}`);
            })
            .finally(() => {
                setChangeLoading(false);
            });
    };

    const handleModalOpen = (subscription) => {
        setSelectedSubscription(subscription);
        setSelectedProductId(subscription.productId); // 현재 상품으로 초기화
        setSelectedIncludeUnderbodyWash(Boolean(subscription.includeUnderbodyWash));
        setIsModalOpen(true);
    };

    const handleCardModalOpen = (subscription) => {
        setSelectedSubscriptionForCard(subscription);
        setSelectedCardId(subscription.cardId || null);
        setIsCardModalOpen(true);
    };

    const handleCardModalClose = () => {
        setIsCardModalOpen(false);
        setSelectedSubscriptionForCard(null);
        setSelectedCardId(null);
        setChangeCardLoading(false);
    };

    const handleChangeCardConfirm = () => {
        if (!selectedSubscriptionForCard || !selectedCardId) return;
        setChangeCardLoading(true);
        subApi.patch(`/admin/subscriptions/${selectedSubscriptionForCard._id}/payment-method`, {
            cardId: selectedCardId
        })
            .then(() => {
                alert('결제 카드가 변경되었습니다.');
                handleCardModalClose();
                // 구독 목록 새로고침
                return subApi.get('/admin/subscriptions?limit=1000')
                    .then(res => {
                        const allSubs = Array.isArray(res.data)
                            ? res.data
                            : (res.data && res.data.subscriptions) || [];
                        const memberSubscriptions = allSubs.filter(sub => {
                            const userIdMatch = sub.userId && (
                                sub.userId._id === memberId || 
                                sub.userId === memberId ||
                                (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                            );
                            return userIdMatch && (sub.status === 'active' || sub.status === 'cancellation_requested');
                        });
                        setSubscriptions(memberSubscriptions);
                    });
            })
            .catch(err => {
                console.log('카드 변경 실패:', err);
                alert(`카드 변경에 실패했습니다.\n${formatAdminApiError(err)}`);
            })
            .finally(() => {
                setChangeCardLoading(false);
            });
    };

    // 구독 활성화 관련 핸들러들
    const handleActivateSubscription = (vehicle) => {
        setSelectedVehicleForActivation(vehicle);
        setActivationProductId(null);
        setActivationCardId('none'); // 'none'으로 설정하여 "카드 없음" 옵션 선택
        setActivationIncludeUnderbodyWash(false);
        setIsActivationModalOpen(true);
    };

    const handleActivationModalClose = () => {
        setIsActivationModalOpen(false);
        setSelectedVehicleForActivation(null);
        setActivationProductId(null);
        setActivationCardId('none');
        setActivationIncludeUnderbodyWash(false);
        setActivationLoading(false);
    };

    const handleActivationConfirm = () => {
        if (!selectedVehicleForActivation || !activationProductId) {
            alert('상품을 선택해주세요.');
            return;
        }

        setActivationLoading(true);

        // 구독 생성 API 호출 (카드 없이 영구 구독)
        const subscriptionData = {
            userId: memberId,
            vehicleId: selectedVehicleForActivation._id,
            productId: activationProductId,
            includeUnderbodyWash: Boolean(activationIncludeUnderbodyWash),
            // 관리자 권한으로 카드 없이 영구 구독 설정
            paymentType: 'admin_managed', // 관리자 관리 구독
            isPermanent: true, // 영구 구독
            status: 'active' // 즉시 활성화
        };

        // 카드가 선택된 경우에만 cardId 추가 ('none'이 아닌 경우)
        if (activationCardId && activationCardId !== 'none') {
            subscriptionData.cardId = activationCardId;
        }

        subApi.post('/admin/subscriptions', subscriptionData)
            .then(() => {
                alert('구독이 활성화되었습니다.');
                handleActivationModalClose();
                // 구독 목록 새로고침
                return subApi.get('/admin/subscriptions?limit=1000')
                    .then(res => {
                        const allSubs = Array.isArray(res.data)
                            ? res.data
                            : (res.data && res.data.subscriptions) || [];
                        const memberSubscriptions = allSubs.filter(sub => {
                            const userIdMatch = sub.userId && (
                                sub.userId._id === memberId || 
                                sub.userId === memberId ||
                                (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                            );
                            return userIdMatch && (sub.status === 'active' || sub.status === 'cancellation_requested');
                        });
                        setSubscriptions(memberSubscriptions);
                    });
            })
            .catch(err => {
                console.log('구독 활성화 실패:', err);
                alert(`구독 활성화에 실패했습니다.\n${formatAdminApiError(err)}`);
            })
            .finally(() => {
                setActivationLoading(false);
            });
    };

    // 구독 해지 처리
    const handleCancelSubscription = (subscription) => {
        if (!window.confirm('정말로 이 구독을 해지하시겠습니까?')) {
            return;
        }

        const isPermanent = subscription.lastPaymentResult?.isPermanent || 
                           subscription.lastPaymentResult?.paymentType === 'admin_managed';

        const cancelMessage = isPermanent 
            ? '영구 구독을 해지하시겠습니까?'
            : '구독을 해지하시겠습니까?';

        if (!window.confirm(cancelMessage)) {
            return;
        }

        subApi.post(`/admin/subscriptions/${subscription._id}/cancel-immediate`)
            .then(() => {
                alert('구독이 해지되었습니다.');
                // 구독 목록 새로고침
                return subApi.get('/admin/subscriptions?limit=1000')
                    .then(res => {
                        const allSubs = Array.isArray(res.data)
                            ? res.data
                            : (res.data && res.data.subscriptions) || [];
                        const memberSubscriptions = allSubs.filter(sub => {
                            const userIdMatch = sub.userId && (
                                sub.userId._id === memberId ||
                                sub.userId === memberId ||
                                (typeof sub.userId === 'object' && sub.userId._id && sub.userId._id === memberId)
                            );
                            return userIdMatch && (sub.status === 'active' || sub.status === 'cancellation_requested');
                        });
                        setSubscriptions(memberSubscriptions);
                    });
            })
            .catch(err => {
                console.log('구독 해지 실패:', err);
                alert(`구독 해지에 실패했습니다.\n${formatAdminApiError(err)}`);
            });
    };

    if (loading) {
        return <Loading text="멤버 차량 목록을 불러오는 중..." />;
    }

    if (!member) {
        return (
            <div style={{ maxWidth: 1000, margin: '40px auto 0 auto', padding: '0 16px', textAlign: 'center' }}>
                멤버를 찾을 수 없습니다.
            </div>
        );
    }



    return (
        <div style={{ maxWidth: 1000, margin: '40px auto 0 auto', padding: '0 16px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 8, color: '#222', margin: 0 }}>
                멤버 상세 정보
            </h2>

            {/* 멤버 정보 */}
            <div style={{ 
                marginTop: '20px', 
                background: '#fff', 
                borderRadius: 8, 
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: '20px'
            }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1976d2', marginBottom: '12px' }}>
                    멤버 정보
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    <div>
                        <span style={{ fontWeight: 600, color: '#666' }}>이름: </span>
                        <span>{member.memberNickname || '-'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 600, color: '#666' }}>이메일: </span>
                        <span>{member.memberEmail || '-'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 600, color: '#666' }}>전화번호: </span>
                        <span>{member.memberPhone || '-'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 600, color: '#666' }}>포인트: </span>
                        <span>{member.point || 0}점</span>
                    </div>
                </div>
            </div>
                        {/* 멤버 정보 - 조작 버튼 (박스 바깥 우측) */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                    style={{
                        padding: '8px 16px',
                        background: '#1976d2',
                        color: '#fff',
                        border: '1px solid #1976d2',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 14,
                        transition: 'background 0.2s, color 0.2s'
                    }}
                    onMouseOver={e => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.color = '#1976d2';
                    }}
                    onMouseOut={e => {
                        e.currentTarget.style.background = '#1976d2';
                        e.currentTarget.style.color = '#fff';
                    }}
                    onClick={() => setIsEditModalOpen(true)}
                >
                    정보 수정
                </button>
            </div>

            {/* 카드 정보 */}
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1976d2', marginBottom: '12px' }}>
                    등록된 카드 목록
                </h3>
                <div style={{ minHeight: 120 }}>
                    {/** 현재 구독에서 사용 중인 카드 식별용 집합 */}
                    {(() => {
                        return null;
                    })()}
                    {cardsLoading ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: '#666',
                            fontSize: '14px',
                            background: '#fff',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            카드 정보를 불러오는 중...
                        </div>
                    ) : cards.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '40%' }}>카드사</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '40%' }}>카드번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>사용여부</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const usedCardIdSet = new Set();
                                    subscriptions.forEach(sub => {
                                        const cid = sub && sub.cardId ? (sub.cardId._id || sub.cardId) : null;
                                        if (cid) usedCardIdSet.add(cid);
                                    });
                                    return cards.map((card, index) => {
                                        const isUsed = usedCardIdSet.has(card._id);
                                        return (
                                        <tr key={card._id || index} style={{ 
                                            borderBottom: '1px solid #eee',
                                            background: isUsed ? '#f1f8e9' : '#fff',
                                            boxShadow: isUsed ? 'inset 4px 0 0 0 #66bb6a' : 'none'
                                        }}>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center', fontWeight: isUsed ? 600 : 400 }}>
                                                {card.cardCompany || '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center', fontWeight: isUsed ? 600 : 400 }}>
                                                <span>{card.cardNumberMasked || '-'}</span>
                                            </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                                {isUsed ? (
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: 14,
                                                        backgroundColor: '#e8f5e9',
                                                        color: '#2e7d32',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        border: '1px solid #c8e6c9'
                                                    }}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            width: 8,
                                                            height: 8,
                                                            backgroundColor: '#2e7d32',
                                                            borderRadius: '50%'
                                                        }} />
                                                        사용중
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                    });
                                })()}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: '#666',
                            fontSize: '14px',
                            background: '#fff',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            카드 정보를 불러올 수 없습니다.
                        </div>
                    )}
                </div>
            </div>

            {/* 구독 정보 */}
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1976d2', marginBottom: '12px' }}>
                    구독 정보
                </h3>
                <div style={{ minHeight: 120 }}>
                    {subscriptions.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>차량번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>상품</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '14%' }}>상태</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '16%' }}>정기결제 메모</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '12%' }}>만료일</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '24%' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptions.map((subscription, index) => (
                                    <tr key={subscription._id || index} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {(() => {
                                                // 멤버의 차량 목록에서 해당 구독의 차량 찾기
                                                const vehicle = vehicles.find(v => 
                                                    v._id === subscription.vehicleId || 
                                                    (subscription.vehicleId && subscription.vehicleId._id === v._id)
                                                );
                                                return vehicle ? vehicle.licensePlate : '-';
                                            })()}
                                        </td>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {subscription.productId === 'daily_subscription' ? '데일리' : 
                                             subscription.productId === 'all_in_one_subscription' ? '올인원' :
                                             subscription.productId}
                                        </td>
                                        <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                backgroundColor: 
                                                    subscription.status === 'active' ? '#e8f5e8' :
                                                    subscription.status === 'cancellation_requested' ? '#fff3e0' :
                                                    subscription.status === 'cancelled' ? '#ffe8e8' :
                                                    subscription.status === 'refunded' ? '#f3e5f5' : '#ffe8e8',
                                                color: 
                                                    subscription.status === 'active' ? '#2e7d32' :
                                                    subscription.status === 'cancellation_requested' ? '#f57c00' :
                                                    subscription.status === 'cancelled' ? '#d32f2f' :
                                                    subscription.status === 'refunded' ? '#7b1fa2' : '#d32f2f'
                                            }}>
                                                {subscription.status === 'active' ? '활성' : 
                                                 subscription.status === 'cancellation_requested' ? '해지예약' :
                                                 subscription.status === 'cancelled' ? '취소' :
                                                 subscription.status === 'refunded' ? '환불' : subscription.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 10px', textAlign: 'center', verticalAlign: 'top' }}>
                                            <MemberSubscriptionPaymentNoteCell subscription={subscription} />
                                        </td>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {subscription.expirationDate ? new Date(subscription.expirationDate).toLocaleDateString('ko-KR') : '-'}
                                        </td>
                                        <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                            <button
                                                style={{
                                                    padding: '6px 12px',
                                                    background: '#1976d2',
                                                    color: '#fff',
                                                    border: '1px solid #1976d2',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    transition: 'background 0.2s, color 0.2s',
                                                }}
                                                onMouseOver={e => {
                                                    e.currentTarget.style.background = '#fff';
                                                    e.currentTarget.style.color = '#1976d2';
                                                }}
                                                onMouseOut={e => {
                                                    e.currentTarget.style.background = '#1976d2';
                                                    e.currentTarget.style.color = '#fff';
                                                }}
                                                onClick={() => handleProductChange(subscription)}
                                            >
                                                상품변경
                                            </button>
                                            <button
                                                style={{
                                                    marginLeft: 8,
                                                    padding: '6px 12px',
                                                    background: '#455a64',
                                                    color: '#fff',
                                                    border: '1px solid #455a64',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    transition: 'background 0.2s, color 0.2s',
                                                }}
                                                onMouseOver={e => {
                                                    e.currentTarget.style.background = '#fff';
                                                    e.currentTarget.style.color = '#455a64';
                                                }}
                                                onMouseOut={e => {
                                                    e.currentTarget.style.background = '#455a64';
                                                    e.currentTarget.style.color = '#fff';
                                                }}
                                                onClick={() => handleCardModalOpen(subscription)}
                                            >
                                                카드변경
                                            </button>
                                            {/* 영구구독일 때만 해지 버튼 표시 */}
                                            {(() => {
                                                const isPermanent = subscription.lastPaymentResult?.isPermanent || 
                                                                   subscription.lastPaymentResult?.paymentType === 'admin_managed';
                                                return isPermanent ? (
                                                    <button
                                                        style={{
                                                            marginLeft: 8,
                                                            padding: '6px 12px',
                                                            background: '#d32f2f',
                                                            color: '#fff',
                                                            border: '1px solid #d32f2f',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 14,
                                                            transition: 'background 0.2s, color 0.2s',
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.color = '#d32f2f';
                                                        }}
                                                        onMouseOut={e => {
                                                            e.currentTarget.style.background = '#d32f2f';
                                                            e.currentTarget.style.color = '#fff';
                                                        }}
                                                        onClick={() => handleCancelSubscription(subscription)}
                                                    >
                                                        해지
                                                    </button>
                                                ) : null;
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: '#666',
                            fontSize: '14px',
                            background: '#fff',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            활성 구독이 없습니다.
                        </div>
                    )}
                </div>
            </div>

            {/* 결제 시도 이력 */}
            <MemberPaymentAttemptsSection memberId={memberId} />

            {/* 차량 목록 */}
            <div style={{ minHeight: 220 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1976d2', marginBottom: '12px' }}>
                    등록된 차량 목록
                </h3>
                {vehicles.length > 0 ? (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>차량번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>차종</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>색상</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '20%' }}>등록일</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '15%' }}>구독상태</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '5%' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map((vehicle, index) => {
                                    // 해당 차량의 구독 찾기 (null 체크 추가)
                                    console.log(`차량 ${vehicle.licensePlate} (ID: ${vehicle._id}) 구독 찾는 중...`);
                                    console.log('현재 구독 목록:', subscriptions);
                                    
                                    const vehicleSubscription = subscriptions.find(sub => {
                                        const vehicleIdMatch = sub.vehicleId && (
                                            sub.vehicleId._id === vehicle._id || 
                                            sub.vehicleId === vehicle._id ||
                                            (typeof sub.vehicleId === 'string' && sub.vehicleId === vehicle._id.toString())
                                        );
                                        console.log(`구독 ${sub._id}: vehicleId=${sub.vehicleId}, 차량ID=${vehicle._id}, 매치=${vehicleIdMatch}`);
                                        return vehicleIdMatch;
                                    });
                                    
                                    console.log(`차량 ${vehicle.licensePlate}의 구독:`, vehicleSubscription);
                                    
                                    return (
                                    <tr key={vehicle._id || index} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {vehicle.licensePlate || '-'}
                                        </td>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {vehicle.model || '-'}
                                        </td>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {vehicle.color || '-'}
                                        </td>
                                        <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                            {vehicle.createdAt ? new Date(vehicle.createdAt).toLocaleDateString('ko-KR') : '-'}
                                        </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                                {vehicleSubscription ? (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        backgroundColor: 
                                                            vehicleSubscription.status === 'active' ? '#e8f5e8' :
                                                            vehicleSubscription.status === 'cancellation_requested' ? '#fff3e0' :
                                                            vehicleSubscription.status === 'cancelled' ? '#ffe8e8' :
                                                            vehicleSubscription.status === 'refunded' ? '#f3e5f5' : '#ffe8e8',
                                                        color: 
                                                            vehicleSubscription.status === 'active' ? '#2e7d32' :
                                                            vehicleSubscription.status === 'cancellation_requested' ? '#f57c00' :
                                                            vehicleSubscription.status === 'cancelled' ? '#d32f2f' :
                                                            vehicleSubscription.status === 'refunded' ? '#7b1fa2' : '#d32f2f'
                                                    }}>
                                                        {vehicleSubscription.productId === 'daily_subscription' ? '데일리' : 
                                                         vehicleSubscription.productId === 'all_in_one_subscription' ? '올인원' :
                                                         vehicleSubscription.productId}
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#666'
                                                    }}>
                                                        구독없음
                                                    </span>
                                                )}
                                            </td>
                                        <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                                {!vehicleSubscription ? (
                                                    <button
                                                        style={{
                                                            padding: '6px 12px',
                                                            background: '#4caf50',
                                                            color: '#fff',
                                                            border: '1px solid #4caf50',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 14,
                                                            transition: 'background 0.2s, color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 70,
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.color = '#4caf50';
                                                        }}
                                                        onMouseOut={e => {
                                                            e.currentTarget.style.background = '#4caf50';
                                                            e.currentTarget.style.color = '#fff';
                                                        }}
                                                        onClick={() => handleActivateSubscription(vehicle)}
                                                    >
                                                        구독활성화
                                                    </button>
                                                ) : null}
                                                <button
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: '#d32f2f',
                                                        color: '#fff',
                                                        border: '1px solid #d32f2f',
                                                        borderRadius: 4,
                                                        cursor: 'pointer',
                                                        fontSize: 14,
                                                        transition: 'background 0.2s, color 0.2s',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: 48,
                                                    }}
                                                    onMouseOver={e => {
                                                        e.currentTarget.style.background = '#fff';
                                                        e.currentTarget.style.color = '#d32f2f';
                                                    }}
                                                    onMouseOut={e => {
                                                        e.currentTarget.style.background = '#d32f2f';
                                                        e.currentTarget.style.color = '#fff';
                                                    }}
                                                    onClick={() => {
                                                        if (window.confirm('이 차량을 삭제하시겠습니까?')) {
                                                            api.delete(`/member/admin/members/${memberId}/vehicles/${vehicle._id}`)
                                                                .then(() => {
                                                                    setVehicles(prev => prev.filter(v => (v._id || v.id) !== (vehicle._id || vehicle.id)));
                                                                    alert('차량이 삭제되었습니다.');
                                                                })
                                                                .catch(err => {
                                                                    console.log('차량 삭제 실패:', err);
                                                                    alert(err?.response?.data?.message || '차량 삭제에 실패했습니다.');
                                                                });
                                                        }
                                                    }}
                                                >
                                                    삭제
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#666',
                        fontSize: '14px',
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        등록된 차량이 없습니다.
                    </div>
                )}
            </div>

            {/* 뒤로가기 버튼 */}
            <div style={{ textAlign: 'right', marginTop: 20 }}>
                <button
                    style={{
                        padding: '8px 20px',
                        background: '#1976d2',
                        color: '#fff',
                        border: '1px solid #1976d2',
                        borderRadius: 4,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background 0.2s, color 0.2s',
                    }}
                    onMouseOver={e => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.color = '#1976d2';
                        e.currentTarget.style.border = '1px solid #1976d2';
                    }}
                    onMouseOut={e => {
                        e.currentTarget.style.background = '#1976d2';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.border = '1px solid #1976d2';
                    }}
                    onClick={() => nav('/admin/members')}
                >
                    목록으로
                </button>
            </div>
            
            {/* 상품 변경 모달 */}
            {isModalOpen && selectedSubscription && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 8,
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h3 style={{ 
                            fontSize: 20, 
                            fontWeight: 600, 
                            color: '#1976d2', 
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            상품 변경
                        </h3>
                        
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>차량번호: </span>
                                <span>{(() => {
                                    // 멤버의 차량 목록에서 해당 구독의 차량 찾기
                                    const vehicle = vehicles.find(v => 
                                        v._id === selectedSubscription.vehicleId || 
                                        (selectedSubscription.vehicleId && selectedSubscription.vehicleId._id === v._id)
                                    );
                                    return vehicle ? vehicle.licensePlate : '-';
                                })()}</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>현재 상품: </span>
                                <span style={{ 
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32'
                                }}>
                                    {selectedSubscription.productId === 'daily_subscription' ? '데일리' : 
                                     selectedSubscription.productId === 'all_in_one_subscription' ? '올인원' :
                                     selectedSubscription.productId}
                                </span>
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: '12px' }}>
                                변경할 상품을 선택하세요:
                            </h4>
                            {productsLoading ? (
                                <div style={{ fontSize: 13, color: '#666' }}>상품 정보를 불러오는 중...</div>
                            ) : productsError ? (
                                <div style={{ fontSize: 13, color: '#d32f2f' }}>{productsError}</div>
                            ) : (
                                <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                                    {products.map((p) => (
                                        <button
                                            key={p.id}
                                            style={{
                                                padding: '12px 16px',
                                                background: selectedProductId === p.id ? '#1976d2' : '#f5f5f5',
                                                color: selectedProductId === p.id ? '#fff' : '#666',
                                                border: '1px solid #ddd',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                fontWeight: 500,
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => setSelectedProductId(p.id)}
                                        >
                                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{p.name}</div>
                                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                                                {(p.price || 0).toLocaleString('ko-KR')}원/월
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(selectedIncludeUnderbodyWash)}
                                    onChange={(e) => setSelectedIncludeUnderbodyWash(e.target.checked)}
                                />
                                <span style={{ fontSize: 14, color: '#333' }}>하부세차 포함</span>
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: '#fff',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleModalClose}
                            >
                                취소
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: selectedProductId && selectedProductId !== selectedSubscription.productId && !changeLoading ? '#1976d2' : '#ccc',
                                    color: '#fff',
                                    border: '1px solid #1976d2',
                                    borderRadius: 4,
                                    cursor: selectedProductId && selectedProductId !== selectedSubscription.productId && !changeLoading ? 'pointer' : 'not-allowed',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => {
                                    if (selectedProductId && selectedProductId !== selectedSubscription.productId && !changeLoading) {
                                        handleProductChangeConfirm(selectedProductId);
                                    }
                                }}
                                disabled={!selectedProductId || selectedProductId === selectedSubscription.productId || changeLoading}
                            >
                                {changeLoading ? '처리 중...' : '변경'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 카드 변경 모달 */}
            {isCardModalOpen && selectedSubscriptionForCard && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 8,
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h3 style={{ 
                            fontSize: 20, 
                            fontWeight: 600, 
                            color: '#1976d2', 
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            결제 카드 변경
                        </h3>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>차량번호: </span>
                                <span>{(() => {
                                    const vehicle = vehicles.find(v => 
                                        v._id === selectedSubscriptionForCard.vehicleId || 
                                        (selectedSubscriptionForCard.vehicleId && selectedSubscriptionForCard.vehicleId._id === v._id)
                                    );
                                    return vehicle ? vehicle.licensePlate : '-';
                                })()}</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#666' }}>
                                아래에서 사용할 카드를 선택하세요.
                            </div>
                        </div>

                        <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', borderRadius: 6, marginBottom: 16 }}>
                            {cardsLoading ? (
                                <div style={{ padding: 16, fontSize: 14, color: '#666' }}>카드 정보를 불러오는 중...</div>
                            ) : (cards && cards.length > 0) ? (
                                cards.map((c) => (
                                    <label key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="card"
                                            checked={selectedCardId === c._id}
                                            onChange={() => setSelectedCardId(c._id)}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#333' }}>{c.cardCompany || '-'}</div>
                                                <div style={{ fontSize: 12, color: '#666' }}>{c.cardNumberMasked || '-'}</div>
                                            </div>
                                            {selectedSubscriptionForCard && selectedSubscriptionForCard.cardId === c._id && (
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: 12,
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontSize: 12,
                                                    fontWeight: 600
                                                }}>
                                                    현재 설정된 카드
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                ))
                            ) : (
                                <div style={{ padding: 16, fontSize: 14, color: '#666' }}>등록된 카드가 없습니다.</div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: '#fff',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleCardModalClose}
                            >
                                취소
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: selectedCardId && !changeCardLoading ? '#1976d2' : '#ccc',
                                    color: '#fff',
                                    border: '1px solid #1976d2',
                                    borderRadius: 4,
                                    cursor: selectedCardId && !changeCardLoading ? 'pointer' : 'not-allowed',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => {
                                    if (selectedCardId && !changeCardLoading) {
                                        handleChangeCardConfirm();
                                    }
                                }}
                                disabled={!selectedCardId || changeCardLoading}
                            >
                                {changeCardLoading ? '처리 중...' : '변경'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 구독 활성화 모달 */}
            {isActivationModalOpen && selectedVehicleForActivation && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 8,
                        padding: '24px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h3 style={{ 
                            fontSize: 20, 
                            fontWeight: 600, 
                            color: '#1976d2', 
                            marginBottom: '12px',
                            textAlign: 'center'
                        }}>
                            구독 활성화
                        </h3>
                        <div style={{ 
                            fontSize: 14, 
                            color: '#666', 
                            textAlign: 'center',
                            marginBottom: '20px',
                            padding: '8px 12px',
                            backgroundColor: '#f1f8e9',
                            borderRadius: '4px',
                            border: '1px solid #c8e6c9'
                        }}>
                            💡 관리자 권한으로 카드 없이도 영구 구독을 활성화할 수 있습니다
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>차량번호: </span>
                                <span>{selectedVehicleForActivation.licensePlate}</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>차종: </span>
                                <span>{selectedVehicleForActivation.model || '-'}</span>
                            </div>
                        </div>

                        {/* 상품 선택 */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: '12px' }}>
                                상품을 선택하세요:
                            </h4>
                            {productsLoading ? (
                                <div style={{ fontSize: 13, color: '#666' }}>상품 정보를 불러오는 중...</div>
                            ) : productsError ? (
                                <div style={{ fontSize: 13, color: '#d32f2f' }}>{productsError}</div>
                            ) : (
                                <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                                    {products.map((p) => (
                                        <button
                                            key={p.id}
                                            style={{
                                                padding: '12px 16px',
                                                background: activationProductId === p.id ? '#1976d2' : '#f5f5f5',
                                                color: activationProductId === p.id ? '#fff' : '#666',
                                                border: '1px solid #ddd',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                fontWeight: 500,
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => setActivationProductId(p.id)}
                                        >
                                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{p.name}</div>
                                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                                                {(p.price || 0).toLocaleString('ko-KR')}원/월
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 결제 카드 선택 (선택사항) */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: '12px' }}>
                                결제 카드 선택 (선택사항):
                            </h4>
                            <div style={{ fontSize: 13, color: '#666', marginBottom: '8px' }}>
                                카드를 선택하지 않으면 관리자 관리 영구 구독으로 설정됩니다.
                            </div>
                            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                                {/* 카드 없음 옵션 */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: activationCardId === 'none' ? '#f1f8e9' : '#fff' }}>
                                    <input
                                        type="radio"
                                        name="activationCard"
                                        checked={activationCardId === 'none'}
                                        onChange={() => setActivationCardId('none')}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#333' }}>카드 없음 (관리자 관리)</div>
                                        <div style={{ fontSize: 12, color: '#666' }}>영구 구독, 자동 결제 없음</div>
                                    </div>
                                </label>
                                {cardsLoading ? (
                                    <div style={{ padding: 16, fontSize: 14, color: '#666' }}>카드 정보를 불러오는 중...</div>
                                ) : (cards && cards.length > 0) ? (
                                    cards.map((c) => (
                                        <label key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                                            <input
                                                type="radio"
                                                name="activationCard"
                                                checked={activationCardId === c._id}
                                                onChange={() => setActivationCardId(c._id)}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#333' }}>{c.cardCompany || '-'}</div>
                                                <div style={{ fontSize: 12, color: '#666' }}>{c.cardNumberMasked || '-'}</div>
                                            </div>
                                        </label>
                                    ))
                                ) : (
                                    <div style={{ padding: 16, fontSize: 14, color: '#666' }}>등록된 카드가 없습니다.</div>
                                )}
                            </div>
                        </div>

                        {/* 하부세차 포함 옵션 */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(activationIncludeUnderbodyWash)}
                                    onChange={(e) => setActivationIncludeUnderbodyWash(e.target.checked)}
                                />
                                <span style={{ fontSize: 14, color: '#333' }}>하부세차 포함</span>
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: '#fff',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleActivationModalClose}
                            >
                                취소
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    background: activationProductId && !activationLoading ? '#4caf50' : '#ccc',
                                    color: '#fff',
                                    border: '1px solid #4caf50',
                                    borderRadius: 4,
                                    cursor: activationProductId && !activationLoading ? 'pointer' : 'not-allowed',
                                    fontSize: 14,
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleActivationConfirm}
                                disabled={!activationProductId || activationLoading}
                            >
                                {activationLoading ? '처리 중...' : '활성화'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 멤버 정보 수정 모달 */}
            {isEditModalOpen && (
                <EditMemberModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={() => {
                        refreshMember();
                    }}
                    memberId={memberId}
                />
            )}


        </div>
    );
};

export default MemberVehicles; 

// 모달 렌더링
/* 아래 위치에 추가 렌더가 필요하므로 파일 하단 export 위로 옮기지 않고, 컴포넌트 반환부 아래에서 렌더링 */